import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Fuse from 'fuse.js';
import './SearchBar.css';

/**
 * Searches the current node subset, or nodes/links/communities in Network view.
 * @param {Function} handleNodeSelect - Function to handle node selection.
 * @param {Function} handleLinkSelect - Function to handle link selection.
 * @param {Object} graphData - The graph data containing nodes and links.
 * @param {Array} communities - The list of communities to search through.
 * @param {Function} selectCommunity - Function to select a community.
 * @returns {JSX.Element} - The rendered SearchBar component.
 */
export default function SearchBar({ nodesOnly = false, inline = false, handleNodeSelect, handleLinkSelect, graphData, communities, selectCommunity }) {
    // State for search input and suggestions
    const [input, setInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchRef = useRef(null);
    const debounceRef = useRef(null);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const [dropdownStyle, setDropdownStyle] = useState(null);
    // Memoize Fuse instance for performance
    const fuse = useMemo(() => new Fuse(
        [
            ...graphData.nodes, 
            ...(nodesOnly ? [] : communities),
        ],
        {
            keys: ['title'], // Adjust keys based on your data structure
            threshold: 0.3, // Adjust threshold for sensitivity
        }
    ), [communities, graphData, nodesOnly]);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        setInput(''); setSuggestions([]); setShowSuggestions(false);
        return () => clearTimeout(debounceRef.current);
    }, [nodesOnly, graphData, communities]);

    // Simple fuzzy search function
    function handleSearch(input) {
        if (nodesOnly && /^(?:comm|link):/i.test(input.trim())) return [];
        if (/^comm:\d+$/i.test(input.trim())) {
            const query = input.replace(/^comm:/i, '').trim();
            // Search for communities whose id is equal to the query
            return communities
            .filter(c => String(c.community).toLowerCase() === query.toLowerCase());
        } else if (/^node:\d+$/i.test(input.trim())) {
            const query = input.replace(/^node:/i, '').trim();
            return graphData.nodes
            .filter(node => String(node.human_readable_id).toLowerCase() === query.toLowerCase());
        } else if (/^link:\d+$/i.test(input.trim())) {
            const query = input.replace(/^link:/i, '').trim();


            return graphData.links
            .filter(link => String(link.human_readable_id).toLowerCase() === query.toLowerCase());
        } else {
            // fuzzy search for title
            const results = fuse.search(input).map(result => result.item);
            const nodeResults = results.filter((item) => "neighbors" in item);
            const communityResults = results.filter((item) => "community" in item);
            return nodesOnly ? nodeResults : [...nodeResults, ...communityResults];
        }
    }

    // Check if the user is searching for a node or community
    function isNode(item) {
        return "neighbors" in item;
    }
    function isCommunity(item) {
        return "community" in item;
    }

    useEffect(() => {
        function handlePointerDown(event) {
            const clickedInsideSearch = searchRef.current && searchRef.current.contains(event.target);
            const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
            if (!clickedInsideSearch && !clickedInsideDropdown) {
                setShowSuggestions(false);
            }
        }

        document.addEventListener('mousedown', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
        };
    }, []);

    useEffect(() => {
        function updateDropdownPosition() {
            if (!inputRef.current) {
                return;
            }

            const rect = inputRef.current.getBoundingClientRect();
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                zIndex: 10000,
            });
        }

        if (showSuggestions) {
            updateDropdownPosition();
            window.addEventListener('resize', updateDropdownPosition);
            window.addEventListener('scroll', updateDropdownPosition, true);
        }

        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [showSuggestions, suggestions.length]);

    // Debounce within this search box; scope changes cancel pending results.
    const handleChange = (e) => {
        const value = e.target.value;
        setInput(value);

        // Clear previous debounce timer
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (value) {
            debounceRef.current = setTimeout(() => {
                const results = handleSearch(value);
                setSuggestions(results);
                setShowSuggestions(true);
            }, 1000);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSelect = (item) => {

        if (isNode(item)) {
            handleNodeSelect(item);
        } else if (isCommunity(item)) {
            selectCommunity(item.community);
        } else {
            handleLinkSelect(item);
        }
        setInput('');
        setSuggestions([]);
        setShowSuggestions(false);  
    };

    return (
        <div
            ref={searchRef}
            className={inline ? 'graph-search-row' : undefined}
            style={inline ? { position: 'relative', width: '100%', minWidth: 0 } : {
            position: 'absolute',
            top: 20,
            right: 30,
            zIndex: 1000,
            width: 250,
        }}>
            <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={handleChange}
                placeholder={nodesOnly ? "Search nodes in this view..." : "Search nodes/links/communities..."}
                style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                }}
                onFocus={() => input && setShowSuggestions(true)}
            />
            {showSuggestions && dropdownStyle && createPortal(
                <ul 
                ref={dropdownRef}
                className='suggestion-list'
                style={dropdownStyle}>
                    {suggestions.map(item => (
                        <li
                            className='suggestion'
                            key={item.id}
                        >
                            <button
                                type="button"
                                className="suggestion-button"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleSelect(item);
                                }}
                            >
                                {isNode(item) ? (
                                    <>
                                        &#128280; {item.title}
                                    </>
                                ) : isCommunity(item) ? (
                                    <>
                                        &#128678; {item.community} - {item.title}
                                    </>
                                ) : (
                                    <>
                                    &#128279; {item.source.title + " - " + item.target.title}
                                    </>
                                )}
                            </button>
                        </li>
                    ))}
                    {suggestions.length === 0 && (
                        <li className='no-suggestions'>{nodesOnly ? "No matching nodes in this view." : "No items found. If you are inside a community, try going back to root and search globally."}</li>
                    )}
                </ul>,
                document.body
            )}
        </div>
    );
}
